import type { ConsoleParameter } from './types';

export type ParamGroup = {
  key: string;
  label: string;
  params: ConsoleParameter[];
  defaultOpen: boolean;
};

const SCORE_PARAMS = new Set([
  'min_score',
  'max_score',
  'min_ei',
  'max_ei',
  'min_dsi',
  'max_dsi',
  'min_dpi',
  'max_dpi',
  'min_pli',
  'max_pli',
  'min_polyphen',
  'max_polyphen',
  'min_sift',
  'max_sift',
  'min_icite_apt',
  'min_beta',
  'max_beta',
  'min_odds_ratio',
  'max_odds_ratio',
  'min_num_shared_genes',
  'min_num_shared_variants',
  'min_jaccard_genes',
  'min_jaccard_variants',
  'min_sokal',
  'minimum_similarity_threshold',
  'min_numCTs',
  'min_tces',
  'max_tces',
]);

const DATE_PARAMS = new Set([
  'min_yearInitial',
  'max_yearInitial',
  'min_yearFinal',
  'max_yearFinal',
  'min_year',
  'max_year',
  'min_pmYear',
  'max_pmYear',
  'min_timestamp',
  'max_timestamp',
  'historical_score_year_min',
  'historical_score_year_max',
]);

const CLASSIFICATION_PARAMS = new Set([
  'type',
  'dis_class_list',
  'disease_prevalence_class',
  'disease_prevalence_geo_area',
  'disease_prevalence_type',
  'disease_inheritance',
  'evidence_level',
  'consequences',
  'clinical_significance',
  'gof_lof',
  'association_type',
  'chemical_association',
  'chemical_effect',
  'chem_molecule_type',
  'polarity',
  'reference_type',
  'ancestry_name',
  'ancestry_id',
  'nct_phase',
  'gender',
  'sex',
  'dmt',
  'disease_class_name_MSH',
  'dda_relation',
]);

const PAGINATION_PARAMS = new Set([
  'page_number',
  'page_size',
  'order_by',
  'order',
  'output',
  'results',
  'pageNumber',
  'pageSize',
]);

function hasTcesParams(params: ConsoleParameter[]): boolean {
  return params.some(p => p.name === 'min_tces' || p.name === 'max_tces');
}

function resolveGroup(param: ConsoleParameter, all: ConsoleParameter[]): string {
  if (param.required || param.in === 'body' || param.in === 'path') return 'required';
  if (SCORE_PARAMS.has(param.name)) return 'scores';
  if (param.name === 'dis_class_list' && hasTcesParams(all)) return 'scores';
  if (DATE_PARAMS.has(param.name)) return 'dates';
  if (CLASSIFICATION_PARAMS.has(param.name)) return 'classification';
  if (PAGINATION_PARAMS.has(param.name)) return 'pagination';
  return 'identifiers';
}

const GROUP_ORDER = ['required', 'identifiers', 'scores', 'dates', 'classification', 'pagination'];

const GROUP_LABELS: Record<string, string> = {
  required: 'Required / Identifiers',
  identifiers: 'Identifiers & Filters',
  scores: 'Score Ranges',
  dates: 'Date Ranges',
  classification: 'Classification & Metadata',
  pagination: 'Pagination & Order',
};

export function groupParameters(params: ConsoleParameter[]): ParamGroup[] {
  const map = new Map<string, ConsoleParameter[]>();
  for (const p of params) {
    const g = resolveGroup(p, params);
    if (!map.has(g)) map.set(g, []);
    map.get(g)!.push(p);
  }

  const requiredParams = map.get('required') ?? [];
  const identifierParams = map.get('identifiers') ?? [];
  if (requiredParams.length === 0 && identifierParams.length > 0) {
    // nothing marked required: promote identifiers to always-visible
    return buildGroups(map, 'identifiers');
  }
  return buildGroups(map, 'required');
}

function buildGroups(map: Map<string, ConsoleParameter[]>, alwaysOpenKey: string): ParamGroup[] {
  return GROUP_ORDER.filter(key => map.has(key) && map.get(key)!.length > 0).map(key => ({
    key,
    label: GROUP_LABELS[key] ?? key,
    params: map.get(key)!,
    defaultOpen: key === alwaysOpenKey,
  }));
}

export function countActiveFilters(params: ConsoleParameter[], values: Record<string, string>): number {
  let count = 0;
  for (const p of params) {
    if (p.in === 'body') continue;
    const v = values[p.name]?.trim();
    if (v) count++;
  }
  return count;
}

export function isMinMaxPair(a: string, b: string): boolean {
  const normalize = (s: string) => s.replace(/^(min_?|max_?)/, '');
  if (!a.startsWith('min') || !b.startsWith('max')) return false;
  return normalize(a) === normalize(b);
}
